/**
 * Recommendation Detail — Review, decide (owner), approve (compliance admin).
 * Shows full context: rationale, matched citations, affected policy, source document.
 */
import React, { useState, useEffect, useCallback } from 'react';
import CounselPanel from './CounselPanel';
import {
  PencilSquareIcon,
  DocumentPlusIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';

const API = process.env.REACT_APP_API_URL;

const TYPE_CFG = {
  update_policy: { label: 'Update Policy', Icon: PencilSquareIcon,    color: 'bg-amber-50 text-amber-700 border-amber-200' },
  create_policy: { label: 'New Policy',    Icon: DocumentPlusIcon,    color: 'bg-blue-50 text-blue-700 border-blue-200' },
  review_policy: { label: 'Review Policy', Icon: MagnifyingGlassIcon, color: 'bg-slate-100 text-slate-600 border-slate-200' },
  no_action:     { label: 'No Action',     Icon: CheckCircleIcon,     color: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
};

export default function RecommendationDetail({ recommendationId, onBack, onOpenPolicy }) {
  const [rec, setRec] = useState(null);
  const [decisions, setDecisions] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [reason, setReason] = useState('');
  const [versionContent, setVersionContent] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(new Date().toISOString().split('T')[0]);
  const [error, setError] = useState('');
  const [redrafting, setRedrafting] = useState(false);
  const [redraftNotes, setRedraftNotes] = useState('');
  // Feedback state
  const [fbRating, setFbRating] = useState(0);
  const [fbUseful, setFbUseful] = useState(null);
  const [fbAccurate, setFbAccurate] = useState(null);
  const [fbSpecific, setFbSpecific] = useState(null);
  const [fbComment, setFbComment] = useState('');
  const [fbSaved, setFbSaved] = useState(false);
  const [fbSaving, setFbSaving] = useState(false);

  const sessionUser = (() => {
    try { return JSON.parse(localStorage.getItem('sessionUser') || 'null'); } catch { return null; }
  })();
  const isComplianceAdmin = ['admin', 'superadmin'].includes(sessionUser?.role) && !sessionUser?.department_id;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/ppm/recommendations/${recommendationId}`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) {
        setRec(data.recommendation);
        setDecisions(data.decisions || []);
        setApprovals(data.approvals || []);
      }
    } catch {}
    setLoading(false);
  }, [recommendationId]);

  useEffect(() => { load(); }, [load]);

  const handleDecide = async (decision) => {
    if (!reason.trim() && decision !== 'proceed') { setError('Please provide a reason'); return; }
    setActing(true); setError('');
    try {
      const res = await fetch(`${API}/api/ppm/recommendations/${recommendationId}/decide`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decision, reason: reason.trim() }),
      });
      const data = await res.json();
      if (data.success) { setReason(''); load(); }
      else setError(data.message || 'Failed');
    } catch { setError('Request failed'); }
    setActing(false);
  };

  const submitFeedback = async () => {
    if (!fbRating) return;
    setFbSaving(true);
    try {
      const res = await fetch(`${API}/api/ppm/recommendations/${recommendationId}/feedback`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rating: fbRating, useful: fbUseful, accurate: fbAccurate,
          specific: fbSpecific, comment: fbComment.trim() || null,
        }),
      });
      const data = await res.json();
      if (data.success) setFbSaved(true);
    } catch {}
    setFbSaving(false);
  };

  const handleApprove = async (decision) => {
    setActing(true); setError('');
    try {
      const body = { decision, reason: reason.trim() };
      if (decision === 'approved' && versionContent.trim()) {
        body.policy_version_content = versionContent.trim();
        body.effective_from = effectiveFrom;
      }
      const res = await fetch(`${API}/api/ppm/recommendations/${recommendationId}/approve`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) { setReason(''); setVersionContent(''); load(); }
      else setError(data.message || 'Failed');
    } catch { setError('Request failed'); }
    setActing(false);
  };

  if (loading) return <div className="p-8 text-slate-400 text-sm">Loading recommendation...</div>;
  if (!rec) return <div className="p-8 text-red-500 text-sm">Not found. <button onClick={onBack} className="underline ml-1">Back</button></div>;

  const typeCfg = TYPE_CFG[rec.type] || TYPE_CFG.review_policy;
  const isPending = rec.status === 'pending';
  const isOwnerDecided = rec.status === 'owner_decided';
  const isTerminal = rec.status === 'approved' || rec.status === 'rejected';
  const ownerProceeded = decisions.some(d => d.decision === 'proceed');

  // Parse rich recommendation detail (section_changes, risk, proposed summary) from ai_analysis
  let aiDetail = {};
  try {
    aiDetail = typeof rec.ai_analysis === 'string' ? JSON.parse(rec.ai_analysis) : (rec.ai_analysis || {});
  } catch { aiDetail = {}; }
  const sectionChanges = Array.isArray(aiDetail.section_changes) ? aiDetail.section_changes : [];
  const risk = aiDetail.risk_if_not_updated;
  const proposedChangeSummary = aiDetail.proposed_change_summary;
  const effectiveDateRec = aiDetail.effective_date_recommendation;
  const operationalEvidence = aiDetail.operational_evidence;
  const operationalSignals = aiDetail.operational_signals;
  const draftedPolicy = aiDetail.auto_drafted_policy;  // for create_policy
  const regQuoteValidation = aiDetail.regulation_quote_validation;

  const handleRedraft = async () => {
    setRedrafting(true);
    try {
      const res = await fetch(`${API}/api/ppm/recommendations/${recommendationId}/draft`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ human_notes: redraftNotes || null }),
      });
      const data = await res.json();
      if (data.success) { setRedraftNotes(''); load(); }
    } catch {}
    setRedrafting(false);
  };

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <button onClick={onBack} className="text-sm text-slate-500 hover:text-slate-700 font-medium mb-4 transition-colors">← Back to inbox</button>

      {/* Header */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6">
        <div className="flex items-center gap-2.5 mb-3 flex-wrap">
          <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${typeCfg.color}`}>{typeCfg.Icon && <typeCfg.Icon className="w-3.5 h-3.5" strokeWidth={1.75} />} {typeCfg.label}</span>
          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${
            rec.confidence === 'high' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
            rec.confidence === 'medium' ? 'bg-amber-50 text-amber-700 border-amber-200' :
            'bg-slate-100 text-slate-500 border-slate-200'
          }`}>{rec.confidence} confidence</span>
          {regQuoteValidation && regQuoteValidation.unverified > 0 && (
            <span className="px-2 py-0.5 rounded-full text-xs font-semibold border bg-amber-50 text-amber-700 border-amber-200"
              title={`${regQuoteValidation.unverified} of ${regQuoteValidation.checked} quoted passages could not be matched word-for-word to the regulation, the policy, or the source document, so this recommendation's confidence was lowered. Verify the quoted language yourself before acting on it.`}>
              {regQuoteValidation.unverified} unverified quote{regQuoteValidation.unverified === 1 ? '' : 's'} — confidence lowered
            </span>
          )}
          <span className="text-xs text-slate-400">from {rec.source_app}</span>
          {rec.sla_due_date && <span className="text-xs text-slate-400">SLA: {rec.sla_due_date}</span>}
        </div>
        <h1 className="text-lg font-bold text-slate-800 mb-2">{rec.summary}</h1>

        {/* Affected policy */}
        {rec.policy_number && (
          <button onClick={() => onOpenPolicy(rec.affected_policy_id)} className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-xl text-xs text-blue-700 font-medium hover:bg-blue-100 transition-colors mb-3">
            Policy: {rec.policy_number} — {rec.policy_title}
          </button>
        )}

        {/* CFR */}
        {rec.cfr_section && (
          <div className="inline-flex px-2.5 py-1 rounded-lg bg-slate-50 border border-slate-200 text-xs font-mono text-slate-600 mb-3 ml-2">
            {rec.cfr_section} — {rec.mandate_title}
          </div>
        )}

        {/* Rationale */}
        <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 mt-3">
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Analysis Rationale</h3>
          <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{rec.rationale}</p>
        </div>

        {/* Extracted citations */}
        {rec.extracted_citations && Array.isArray(JSON.parse(typeof rec.extracted_citations === 'string' ? rec.extracted_citations : JSON.stringify(rec.extracted_citations))) && (
          <div className="mt-3">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Matched Citations</h3>
            <div className="flex flex-wrap gap-2">
              {(typeof rec.extracted_citations === 'string' ? JSON.parse(rec.extracted_citations) : rec.extracted_citations).map((c, i) => (
                <span key={i} className="px-2.5 py-1 rounded-lg bg-blue-50 text-blue-700 text-xs font-medium border border-blue-200">
                  {c.cfr_section || c.original_citation || JSON.stringify(c)}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Proposed Section-Level Redlines (the substantive policy changes) */}
      {sectionChanges.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-bold text-slate-800">Proposed Policy Changes</h3>
              <p className="text-xs text-slate-400 mt-0.5">Section-by-section redline recommendations generated by AI analysis</p>
            </div>
            {sectionChanges.length > 0 && rec.type === 'update_policy' && (
              <button
                onClick={() => {
                  // Compose a full-version draft from the section changes
                  const draft = sectionChanges
                    .map(sc => `## ${sc.section}\n\n${sc.proposed_text}`)
                    .join('\n\n');
                  setVersionContent(draft);
                  if (effectiveDateRec) setEffectiveFrom(effectiveDateRec);
                }}
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 transition-colors"
              >
                Use as version draft
              </button>
            )}
          </div>
          <div className="space-y-4">
            {sectionChanges.map((sc, i) => (
              <div key={i} className="border border-slate-200 rounded-xl overflow-hidden">
                <div className="bg-slate-50 px-4 py-2 border-b border-slate-200">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-semibold text-slate-700">Section:</span>
                    <span className="text-xs font-mono text-slate-800 bg-white px-2 py-0.5 rounded border border-slate-200">{sc.section}</span>
                  </div>
                  {sc.why && <p className="text-[11px] text-slate-500 mt-1">{sc.why}</p>}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-200">
                  <div className="p-4 bg-red-50/30">
                    <div className="text-[10px] font-semibold text-red-700 uppercase tracking-wide mb-2">Current</div>
                    <p className="text-xs text-slate-700 whitespace-pre-wrap leading-relaxed font-mono">{sc.current_text || '—'}</p>
                  </div>
                  <div className="p-4 bg-emerald-50/30">
                    <div className="text-[10px] font-semibold text-emerald-700 uppercase tracking-wide mb-2">Proposed</div>
                    <p className="text-xs text-slate-700 whitespace-pre-wrap leading-relaxed font-mono">{sc.proposed_text || '—'}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {proposedChangeSummary && (
            <div className="mt-4 p-3 rounded-xl bg-blue-50 border border-blue-200">
              <span className="text-[10px] font-semibold text-blue-700 uppercase tracking-wide">Change summary for audit: </span>
              <span className="text-xs text-slate-700">{proposedChangeSummary}</span>
            </div>
          )}
          {effectiveDateRec && (
            <div className="mt-2 text-xs text-slate-500">
              <span className="font-medium">Recommended effective date:</span> {effectiveDateRec}
            </div>
          )}
        </div>
      )}

      {/* Compliance risk if not updated */}
      {risk && (
        <div className="bg-red-50 rounded-2xl border border-red-200 p-4 mb-6">
          <div className="flex items-start gap-2">
            <ExclamationTriangleIcon className="w-5 h-5 text-red-600 flex-shrink-0" strokeWidth={1.75} />
            <div>
              <h3 className="text-xs font-bold text-red-700 uppercase tracking-wide mb-1">Risk if not updated</h3>
              <p className="text-sm text-slate-700 leading-relaxed">{risk}</p>
            </div>
          </div>
        </div>
      )}

      {/* Operational evidence from other compliance apps */}
      {(operationalEvidence || operationalSignals) && (
        <div className="bg-amber-50/40 rounded-2xl border border-amber-200 p-5 mb-6">
          <div className="flex items-start gap-2 mb-3">
            <LinkIcon className="w-5 h-5 text-amber-600 flex-shrink-0" strokeWidth={1.75} />
            <div className="flex-1">
              <h3 className="text-xs font-bold text-amber-800 uppercase tracking-wide mb-1">Operational evidence</h3>
              {operationalEvidence && <p className="text-sm text-slate-700 leading-relaxed">{operationalEvidence}</p>}
            </div>
          </div>
          {operationalSignals && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
              {(operationalSignals.compliance_issues || []).length > 0 && (
                <div className="bg-white rounded-xl border border-slate-200 p-3">
                  <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-2">
                    CIL ({operationalSignals.compliance_issues.length})
                  </div>
                  <div className="space-y-1.5">
                    {operationalSignals.compliance_issues.slice(0, 4).map((i, idx) => (
                      <div key={idx} className="text-xs">
                        <span className="font-mono text-slate-400">[{i.ref}]</span>{' '}
                        <span className="text-slate-700">{i.area || ''}</span>
                        {i.summary && <div className="text-slate-600 line-clamp-1">{i.summary}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {(operationalSignals.fdr_findings || []).length > 0 && (
                <div className="bg-white rounded-xl border border-slate-200 p-3">
                  <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-2">
                    FDR Findings ({operationalSignals.fdr_findings.length})
                  </div>
                  <div className="space-y-1.5">
                    {operationalSignals.fdr_findings.slice(0, 4).map((f, idx) => (
                      <div key={idx} className="text-xs">
                        <span className="font-mono text-slate-400">[{f.ref}]</span>{' '}
                        <span className={`font-medium ${f.severity === 'critical' ? 'text-red-700' : f.severity === 'major' ? 'text-amber-700' : 'text-slate-600'}`}>
                          {f.severity}
                        </span>{' '}
                        <span className="text-slate-700">{f.title}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {(operationalSignals.privacy_incidents || []).length > 0 && (
                <div className="bg-white rounded-xl border border-slate-200 p-3">
                  <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-2">
                    Privacy Incidents ({operationalSignals.privacy_incidents.length})
                  </div>
                  <div className="space-y-1.5">
                    {operationalSignals.privacy_incidents.slice(0, 4).map((p, idx) => (
                      <div key={idx} className="text-xs">
                        <span className="font-mono text-slate-400">[{p.ref}]</span>{' '}
                        <span className="text-slate-700">{p.type || ''} / {p.status}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {(operationalSignals.coa_deficiencies || []).length > 0 && (
                <div className="bg-white rounded-xl border border-slate-200 p-3">
                  <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-2">
                    Audit Deficiencies ({operationalSignals.coa_deficiencies.length})
                  </div>
                  <div className="space-y-1.5">
                    {operationalSignals.coa_deficiencies.slice(0, 4).map((d, idx) => (
                      <div key={idx} className="text-xs">
                        <span className="font-mono text-slate-400">[{d.ref}]</span>{' '}
                        <span className="text-slate-700 line-clamp-1">{d.summary}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* AI-drafted policy for create_policy recommendations */}
      {rec.type === 'create_policy' && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-sm font-bold text-slate-800">AI-drafted Policy</h3>
              <p className="text-xs text-slate-400 mt-0.5">
                {draftedPolicy
                  ? 'Complete draft ready for compliance review. Edit as needed, then use "Approve" below to create the policy version.'
                  : 'Drafting in progress — generates automatically when this recommendation is created. Refresh in a moment.'}
              </p>
            </div>
            {draftedPolicy && rec.type === 'create_policy' && (
              <button onClick={() => {
                  setVersionContent(draftedPolicy.content);
                  if (draftedPolicy.suggested_category) {
                    // (category is applied on policy creation, not here)
                  }
                }}
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors">
                Use as version draft
              </button>
            )}
          </div>

          {draftedPolicy?.warnings?.length > 0 && (
            <div className="mb-4 p-3 rounded-xl bg-amber-50 border border-amber-200">
              <div className="text-[10px] font-semibold text-amber-700 uppercase tracking-wide mb-1">Draft warnings</div>
              <ul className="space-y-0.5">
                {draftedPolicy.warnings.map((w, i) => (
                  <li key={i} className="text-xs text-slate-700 pl-3 relative before:content-['•'] before:absolute before:left-0 before:text-amber-500">{w}</li>
                ))}
              </ul>
            </div>
          )}

          {draftedPolicy && (
            <>
              {(draftedPolicy.suggested_policy_number || draftedPolicy.suggested_category || draftedPolicy.suggested_review_cycle_months) && (
                <div className="mb-3 flex gap-2 flex-wrap">
                  {draftedPolicy.suggested_policy_number && (
                    <span className="px-2.5 py-1 rounded-lg text-xs font-mono bg-slate-50 text-slate-600 border border-slate-200">
                      # {draftedPolicy.suggested_policy_number}
                    </span>
                  )}
                  {draftedPolicy.suggested_category && (
                    <span className="px-2.5 py-1 rounded-lg text-xs font-medium bg-slate-50 text-slate-600 border border-slate-200 uppercase tracking-wide">
                      {draftedPolicy.suggested_category}
                    </span>
                  )}
                  {draftedPolicy.suggested_review_cycle_months && (
                    <span className="px-2.5 py-1 rounded-lg text-xs text-slate-600 bg-slate-50 border border-slate-200">
                      Review every {draftedPolicy.suggested_review_cycle_months} months
                    </span>
                  )}
                </div>
              )}

              <div className="border border-slate-200 rounded-xl max-h-[600px] overflow-y-auto">
                {Array.isArray(draftedPolicy.structure) && draftedPolicy.structure.length > 0 ? (
                  <div className="divide-y divide-slate-100">
                    {draftedPolicy.structure.map((s, i) => (
                      <div key={i} className="p-4">
                        <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wide mb-2">{s.heading}</h4>
                        <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{s.body}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <pre className="p-4 text-xs text-slate-700 whitespace-pre-wrap font-mono leading-relaxed">{draftedPolicy.content}</pre>
                )}
              </div>

              {draftedPolicy.used_style_refs?.length > 0 && (
                <div className="mt-2 text-[11px] text-slate-400">
                  Style matched against: {draftedPolicy.used_style_refs.map(r => r.policy_number).join(', ')}
                </div>
              )}
            </>
          )}

          {/* Redraft with notes */}
          <div className="mt-4 pt-4 border-t border-slate-100">
            <label className="block text-xs font-medium text-slate-500 mb-1">Redraft with additional directives (optional)</label>
            <textarea value={redraftNotes} onChange={e => setRedraftNotes(e.target.value)} rows={2}
              placeholder="e.g., Add a section on BAA requirements and workforce sanctions."
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs text-slate-800 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 mb-2" />
            <button onClick={handleRedraft} disabled={redrafting}
              className="px-4 py-2 rounded-xl bg-slate-800 text-white text-xs font-semibold hover:bg-slate-900 disabled:opacity-50 transition-colors">
              {redrafting ? 'Redrafting...' : (draftedPolicy ? 'Redraft' : 'Draft now')}
            </button>
          </div>
        </div>
      )}

      {/* Decision History */}
      {decisions.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6">
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Owner Decision</h3>
          {decisions.map(d => (
            <div key={d.id} className={`p-3 rounded-xl border mb-2 ${
              d.decision === 'proceed' ? 'bg-emerald-50 border-emerald-200' :
              d.decision === 'reject' ? 'bg-red-50 border-red-200' :
              'bg-amber-50 border-amber-200'
            }`}>
              <div className="flex items-center gap-2 text-sm font-medium">
                <span className="capitalize">{d.decision.replace(/_/g, ' ')}</span>
                <span className="text-xs text-slate-400">by {d.decided_by_name} · {new Date(d.decided_at).toLocaleString()}</span>
              </div>
              {d.reason && <p className="text-xs text-slate-600 mt-1">{d.reason}</p>}
            </div>
          ))}
        </div>
      )}

      {/* Approval History */}
      {approvals.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6">
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Compliance Approval</h3>
          {approvals.map(a => (
            <div key={a.id} className={`p-3 rounded-xl border ${
              a.decision === 'approved' ? 'bg-emerald-50 border-emerald-200' :
              a.decision === 'rejected' ? 'bg-red-50 border-red-200' :
              'bg-amber-50 border-amber-200'
            }`}>
              <div className="flex items-center gap-2 text-sm font-medium">
                <span className="capitalize">{a.decision}</span>
                <span className="text-xs text-slate-400">by {a.approver_name} · {new Date(a.approved_at).toLocaleString()}</span>
              </div>
              {a.reason && <p className="text-xs text-slate-600 mt-1">{a.reason}</p>}
            </div>
          ))}
        </div>
      )}

      {/* Regulatory Counsel — grounded second opinion on this recommendation */}
      <div className="mb-6">
        <CounselPanel
          subjectType="recommendation"
          subjectId={recommendationId}
          title="Regulatory Counsel — second opinion"
          defaultQuestion="Is this recommendation actually required by the cited regulation, and is the proposed change sufficient to comply?"
        />
      </div>

      {/* Feedback — trains the learning engine */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Rate this recommendation</h3>
            <p className="text-xs text-slate-400 mt-0.5">Your feedback trains the analyzer — poorly rated patterns get suppressed in future runs.</p>
          </div>
          {fbSaved && (
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">Saved — thanks</span>
          )}
        </div>
        {!fbSaved && (
          <>
            <div className="flex items-center gap-1.5 mb-3">
              {[1, 2, 3, 4, 5].map(n => (
                <button key={n} onClick={() => setFbRating(n)}
                  className={`w-8 h-8 rounded-lg text-sm font-bold transition-all ${
                    fbRating >= n
                      ? 'bg-gradient-to-r from-amber-400 to-amber-500 text-white shadow'
                      : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
                  }`}>★</button>
              ))}
              <span className="text-xs text-slate-400 ml-2">{fbRating ? `${fbRating}/5` : 'Tap a star'}</span>
            </div>
            <div className="flex gap-2 flex-wrap mb-3">
              {[
                { key: 'useful',   val: fbUseful,   set: setFbUseful,   label: 'Actionable' },
                { key: 'accurate', val: fbAccurate, set: setFbAccurate, label: 'Correctly identified impact' },
                { key: 'specific', val: fbSpecific, set: setFbSpecific, label: 'Specific enough' },
              ].map(opt => (
                <div key={opt.key} className="flex items-center gap-1">
                  <span className="text-xs text-slate-500 mr-1">{opt.label}:</span>
                  <button onClick={() => opt.set(opt.val === true ? null : true)}
                    className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${
                      opt.val === true ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' : 'bg-slate-50 text-slate-400 border border-slate-200 hover:bg-slate-100'
                    }`}>Yes</button>
                  <button onClick={() => opt.set(opt.val === false ? null : false)}
                    className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${
                      opt.val === false ? 'bg-red-100 text-red-700 border border-red-200' : 'bg-slate-50 text-slate-400 border border-slate-200 hover:bg-slate-100'
                    }`}>No</button>
                </div>
              ))}
            </div>
            <textarea value={fbComment} onChange={e => setFbComment(e.target.value)} rows={2}
              placeholder="Optional: what would make this recommendation better?"
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 mb-3" />
            <button onClick={submitFeedback} disabled={!fbRating || fbSaving}
              className="px-4 py-2 rounded-xl bg-slate-800 text-white text-xs font-semibold hover:bg-slate-900 disabled:opacity-40 transition-colors">
              {fbSaving ? 'Saving...' : 'Submit feedback'}
            </button>
          </>
        )}
      </div>

      {/* Action Section */}
      {!isTerminal && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          {error && <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-xs">{error}</div>}

          {/* Owner Decision */}
          {isPending && (
            <>
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Your Decision</h3>
              <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2}
                placeholder="Reason for your decision (required for reject/clarify)..."
                className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-800 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 mb-3" />
              <div className="flex gap-3">
                <button onClick={() => handleDecide('proceed')} disabled={acting}
                  className="px-5 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 transition-colors">
                  Proceed
                </button>
                <button onClick={() => handleDecide('reject')} disabled={acting}
                  className="px-5 py-2.5 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50 transition-colors">
                  Reject
                </button>
                <button onClick={() => handleDecide('request_clarification')} disabled={acting}
                  className="px-5 py-2.5 rounded-xl border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 disabled:opacity-50 transition-colors">
                  Request Clarification
                </button>
              </div>
            </>
          )}

          {/* Compliance Approval */}
          {isOwnerDecided && isComplianceAdmin && (
            <>
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Compliance Final Review</h3>

              {rec.type === 'update_policy' && ownerProceeded && (
                <div className="mb-4">
                  <label className="block text-xs font-medium text-slate-500 mb-1">New policy version content (if approving)</label>
                  <textarea value={versionContent} onChange={e => setVersionContent(e.target.value)} rows={4}
                    placeholder="Enter the updated policy text..."
                    className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-800 focus:ring-2 focus:ring-orange-500 focus:border-orange-500" />
                  <label className="block text-xs font-medium text-slate-500 mt-2 mb-1">Effective from</label>
                  <input type="date" value={effectiveFrom} onChange={e => setEffectiveFrom(e.target.value)}
                    className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-800 focus:ring-2 focus:ring-orange-500 focus:border-orange-500" />
                </div>
              )}

              <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2}
                placeholder="Compliance review notes..."
                className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-800 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 mb-3" />
              <div className="flex gap-3">
                <button onClick={() => handleApprove('approved')} disabled={acting}
                  className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-orange-600 to-amber-500 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity">
                  Approve
                </button>
                <button onClick={() => handleApprove('rejected')} disabled={acting}
                  className="px-5 py-2.5 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50 transition-colors">
                  Reject
                </button>
                <button onClick={() => handleApprove('returned_for_revision')} disabled={acting}
                  className="px-5 py-2.5 rounded-xl border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 disabled:opacity-50 transition-colors">
                  Return for Revision
                </button>
              </div>
            </>
          )}

          {isOwnerDecided && !isComplianceAdmin && (
            <p className="text-sm text-slate-500">Awaiting compliance admin review.</p>
          )}
        </div>
      )}
    </div>
  );
}
